/**
 * Shell navigation state: sidebar personalisation, the current app, and the
 * small search helpers the sidebar filter and the app launcher share.
 *
 * CLAUDE.md §11 asks the left nav for "favorites and recents". Both are per
 * user and purely local — they describe how one person navigates, not tenant
 * data — so they live in local storage rather than costing a round trip on
 * every page load. The same goes for the two things the app-scoped sidebar
 * added on top: which app the user was last in, and which of an app's
 * sections they have folded away.
 *
 * The state is module-level on purpose: the shell, the sidebar, the top bar
 * and the home screen all call `useNavPrefs` and must see one set of values,
 * not four copies that drift. Every caller passes `useUserScope()` — tenant
 * UUID AND user id, because user ids are per-tenant bigints and user 1 exists
 * in every tenant — and `load()` re-reads storage whenever that scope changes.
 * So a different person signing in on the same machine, including the same
 * numeric user id in another tenant, never inherits the previous person's
 * favourites, recents or last app, in storage or in memory.
 */

import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useRoute } from 'vue-router'

import { t } from '@/i18n'
import { readJson, scopedKey, writeJson } from '@/lib/storage'
import { NAV_ROUTE_ALIASES, type NavRouteAlias } from '@/nav/aliases'
import {
  HOME_APP_ID,
  appForPath,
  isExtraneous,
  navFsdOf,
  visibleApps,
  type VisibleApp,
} from '@/nav/apps'
import {
  NAV_GROUPS,
  navKeywords,
  navLabel,
  type NavGroup,
  type NavItem,
  type PermissionCheck,
  visibleItems,
} from '@/nav/items'

const MAX_RECENTS = 6

/**
 * DOM ids the shell's pieces share: the narrow-screen menu button names the
 * sidebar it controls (`aria-controls`), and closing the drawer hands focus
 * back to that button. One declaration so the two ends cannot disagree.
 */
export const SIDENAV_ID = 'app-sidenav'
export const NAV_TOGGLE_ID = 'shell-nav-toggle'

/** `appId → sectionId → open`. Only sections the user (or navigation) has touched are recorded. */
type SectionState = Record<string, Record<string, boolean>>

const collapsed = ref(false)
const favourites = ref<string[]>([])
const recents = ref<string[]>([])
const lastAppId = ref<string>(HOME_APP_ID)
const sections = ref<SectionState>({})
/**
 * The off-canvas sidebar on narrow screens. Deliberately not persisted: a
 * drawer that re-opens itself over the page on the next load would hide the
 * very screen the user came back to.
 */
const drawerOpen = ref(false)

let loadedFor: string | null = null

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
}

/**
 * Storage is user-writable and outlives releases, so every read is shape-checked
 * rather than trusted: a corrupt entry costs the user one preference, never the
 * sidebar.
 */
function sectionState(value: unknown): SectionState {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {}
  const out: SectionState = {}
  for (const [appId, perApp] of Object.entries(value as Record<string, unknown>)) {
    if (perApp === null || typeof perApp !== 'object' || Array.isArray(perApp)) continue
    const entries = Object.entries(perApp as Record<string, unknown>).filter(
      (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
    )
    out[appId] = Object.fromEntries(entries)
  }
  return out
}

function load(userScope: string): void {
  if (loadedFor === userScope) return
  loadedFor = userScope
  collapsed.value = readJson<unknown>(scopedKey('nav.collapsed', userScope), false) === true
  favourites.value = stringList(readJson<unknown>(scopedKey('nav.favourites', userScope), []))
  recents.value = stringList(readJson<unknown>(scopedKey('nav.recents', userScope), []))
  const last = readJson<unknown>(scopedKey('nav.lastApp', userScope), HOME_APP_ID)
  lastAppId.value = typeof last === 'string' && last !== '' ? last : HOME_APP_ID
  sections.value = sectionState(readJson<unknown>(scopedKey('nav.sections', userScope), {}))
  drawerOpen.value = false
}

/**
 * Test seam: forget which user's preferences are loaded, so the next
 * `useNavPrefs` call re-reads storage. The state is module-level by design
 * (see the file header), which means a test that clears `localStorage` would
 * otherwise still see the previous test's favourites and folded sections.
 */
export function resetNavPrefsForTests(): void {
  loadedFor = null
}

export interface NavPrefs {
  collapsed: Ref<boolean>
  toggleCollapsed: () => void
  favourites: ComputedRef<NavItem[]>
  recents: ComputedRef<NavItem[]>
  isFavourite: (id: string) => boolean
  toggleFavourite: (id: string) => void
  noteVisit: (id: string) => void
  /** The app the user was last inside — the fallback for routes no app lists. */
  lastAppId: Readonly<Ref<string>>
  setLastApp: (id: string) => void
  /**
   * Whether a sidebar section is expanded. `fallback` is what the sidebar
   * shows for a section the user has never toggled.
   */
  isSectionOpen: (appId: string, sectionId: string, fallback: boolean) => boolean
  setSectionOpen: (appId: string, sectionId: string, open: boolean) => void
  /**
   * Expand or collapse many sections at once ("Expand all / Collapse all") —
   * one state change and one storage write, not one per section.
   */
  setSectionsOpen: (appId: string, sectionIds: readonly string[], open: boolean) => void
  /** Narrow-viewport sidebar drawer. */
  drawerOpen: Ref<boolean>
}

/**
 * @param userScope stable per-person key — pass `useUserScope()`
 *   (`<tenantUuid>:<userId>`), never a bare user id, which collides across
 *   tenants — so preferences do not leak between accounts on a shared machine.
 * @param can permission predicate — a favourite for a module the user has since
 *   lost access to must not reappear in the sidebar.
 */
export function useNavPrefs(userScope: ComputedRef<string>, can: PermissionCheck): NavPrefs {
  load(userScope.value)
  watch(userScope, (scope) => {
    loadedFor = null
    load(scope)
  })

  const available = computed(() => visibleItems(NAV_GROUPS, can))

  function byId(ids: readonly string[]): NavItem[] {
    return ids
      .map((id) => available.value.find((item) => item.id === id))
      .filter((item): item is NavItem => item !== undefined)
  }

  function persist(): void {
    writeJson(scopedKey('nav.favourites', userScope.value), favourites.value)
    writeJson(scopedKey('nav.recents', userScope.value), recents.value)
  }

  return {
    collapsed,
    toggleCollapsed: () => {
      collapsed.value = !collapsed.value
      writeJson(scopedKey('nav.collapsed', userScope.value), collapsed.value)
    },
    favourites: computed(() => byId(favourites.value)),
    recents: computed(() => byId(recents.value)),
    isFavourite: (id: string) => favourites.value.includes(id),
    toggleFavourite: (id: string) => {
      favourites.value = favourites.value.includes(id)
        ? favourites.value.filter((candidate) => candidate !== id)
        : [...favourites.value, id]
      persist()
    },
    noteVisit: (id: string) => {
      recents.value = [id, ...recents.value.filter((candidate) => candidate !== id)].slice(
        0,
        MAX_RECENTS,
      )
      persist()
    },
    lastAppId,
    setLastApp: (id: string) => {
      if (lastAppId.value === id) return
      lastAppId.value = id
      writeJson(scopedKey('nav.lastApp', userScope.value), id)
    },
    isSectionOpen: (appId: string, sectionId: string, fallback: boolean) =>
      sections.value[appId]?.[sectionId] ?? fallback,
    setSectionOpen: (appId: string, sectionId: string, open: boolean) => {
      if (sections.value[appId]?.[sectionId] === open) return
      sections.value = {
        ...sections.value,
        [appId]: { ...sections.value[appId], [sectionId]: open },
      }
      writeJson(scopedKey('nav.sections', userScope.value), sections.value)
    },
    setSectionsOpen: (appId: string, sectionIds: readonly string[], open: boolean) => {
      const perApp = sections.value[appId] ?? {}
      if (sectionIds.every((id) => perApp[id] === open)) return
      sections.value = {
        ...sections.value,
        [appId]: { ...perApp, ...Object.fromEntries(sectionIds.map((id) => [id, open])) },
      }
      // Same per-user scoped key as `setSectionOpen`: one person's folds never
      // reach the next person on this machine.
      writeJson(scopedKey('nav.sections', userScope.value), sections.value)
    },
    drawerOpen,
  }
}

/* ------------------------------------------------------------ current app */

export interface CurrentApp {
  /** Every app the user may open, each with its permission-filtered part. */
  apps: ComputedRef<VisibleApp[]>
  /**
   * The app the current route belongs to. `undefined` only for an account
   * that can open nothing at all — Home's dashboard needs no permission, so
   * in practice every signed-in user has at least Home.
   */
  current: ComputedRef<VisibleApp | undefined>
  /** The home screen is a launcher: no sidebar, no app chip, no breadcrumb. */
  onHomeScreen: ComputedRef<boolean>
}

/**
 * Which app the user is "in".
 *
 * The route decides when it can (`appForPath`, preferring the app the user
 * was already in for a screen two apps share). When it cannot — an error
 * page, a deep link no menu lists — the user stays in the app they were in,
 * because yanking the sidebar to Home on a 404 would lose their place. An app
 * the user cannot open is never "current": they would get a sidebar of
 * screens that all refuse them.
 */
export function useCurrentApp(userScope: ComputedRef<string>, can: PermissionCheck): CurrentApp {
  const route = useRoute()
  const prefs = useNavPrefs(userScope, can)
  const apps = computed(() => visibleApps(can))

  const current = computed<VisibleApp | undefined>(() => {
    const list = apps.value
    const find = (id: string | undefined): VisibleApp | undefined =>
      id === undefined ? undefined : list.find((entry) => entry.app.id === id)
    const last = prefs.lastAppId.value
    return find(appForPath(route.path, last)?.id) ?? find(last) ?? find(HOME_APP_ID) ?? list[0]
  })

  return {
    apps,
    current,
    onHomeScreen: computed(() => route.path === '/'),
  }
}

/* ------------------------------------------------------------- matching */

/**
 * Fold text for menu filtering: case-insensitive and mark-insensitive, so
 * "creances" finds "Créances". For Thaana this also drops the fili (vowel
 * signs), which makes a Dhivehi query match on its consonant skeleton —
 * looser than exact, which is the right side to err on for a menu filter.
 */
export function foldForSearch(text: string): string {
  return text.normalize('NFD').replace(/\p{M}+/gu, '').toLocaleLowerCase().trim()
}

/**
 * How well a nav item matches an already-folded query: 0 for no match, then
 * keyword < anywhere in the label < start of a word < start of the label.
 * Deliberately a substring test rather than the palette's subsequence fuzz —
 * a menu filter that shows "Payroll" for "pyl" reads as broken, not clever.
 */
export function scoreNavItem(item: NavItem, foldedQuery: string): number {
  if (foldedQuery === '') return 1
  const label = foldForSearch(navLabel(item))
  if (label.startsWith(foldedQuery)) return 4
  if (label.split(/[\s/-]+/).some((word) => word.startsWith(foldedQuery))) return 3
  if (label.includes(foldedQuery)) return 2
  const keywords = navKeywords(item) ?? []
  return keywords.some((word) => foldForSearch(word).includes(foldedQuery)) ? 1 : 0
}

/** Whether any of `texts` contains the folded query. */
export function textMatches(foldedQuery: string, ...texts: readonly string[]): boolean {
  return foldedQuery === '' || texts.some((text) => foldForSearch(text).includes(foldedQuery))
}

/**
 * Whether an FSD section number answers a query, segment by segment rather
 * than as a substring: "11.2.1" is 11.2.1 and its sub-sections (11.2.1.3),
 * never 11.2.10–11.2.12; "2" is part 2, not every number with a 2 in it. A
 * query ending in "." ("7.2.") asks for everything beneath that prefix.
 * Feature and requirement codes are not numbers and keep substring matching
 * (partial typing such as "PRC-F0" is useful there).
 */
export function fsdNumberMatches(fsd: string, query: string): boolean {
  const number = foldForSearch(fsd)
  const q = foldForSearch(query)
  if (q === '') return false
  return number === q || number.startsWith(`${q}.`) || (q.endsWith('.') && number.startsWith(q))
}

/**
 * Whether a whole sidebar section answers an already-folded query: by its FSD
 * section number (`7.2.2`), its feature code (`INV-F05`), or its short or full
 * title. People who work from the FSD navigate by those, not by screen labels.
 *
 * A section whose every item is a recorded deviation (s-D.2: users, roles)
 * never matches by number or code — that would present screens with no spec
 * as the implementation of one. Its title still matches as plain text.
 */
export function sectionMatches(section: NavGroup, folded: string): boolean {
  if (folded === '') return true
  const citable = !(section.items.length > 0 && section.items.every(isExtraneous))
  if (citable && section.fsd !== undefined && fsdNumberMatches(section.fsd, folded)) return true
  if (citable && section.code !== undefined && foldForSearch(section.code).includes(folded)) return true
  return textMatches(
    folded,
    t(section.labelKey),
    section.fullTitleKey === undefined ? '' : t(section.fullTitleKey),
  )
}

/**
 * The items of `section` a folded query keeps: all of them when the section
 * itself matches, otherwise those matching by label/keywords or by their own
 * effective FSD number or code (an item may carry a requirement id such as
 * `FND-FR-084` that its section does not). A recorded deviation
 * (`isExtraneous`) matches on label/keywords only: it inherits a number
 * structurally but implements no spec, so it is never found by one.
 */
export function filterSection(section: NavGroup, folded: string): NavItem[] {
  if (sectionMatches(section, folded)) return section.items
  return section.items.filter((item) => {
    if (scoreNavItem(item, folded) > 0) return true
    if (isExtraneous(item)) return false
    const { fsd, code } = navFsdOf(item)
    return (
      (fsd !== undefined && fsdNumberMatches(fsd, folded)) ||
      (code !== undefined && foldForSearch(code).includes(folded))
    )
  })
}

/** Segment-aware prefix test: `/inventory` covers `/inventory/7`, never `/inventory-counts`. */
function underPrefix(path: string, prefix: string): boolean {
  return prefix === '/' ? path === '/' : path === prefix || path.startsWith(`${prefix}/`)
}

/**
 * The item a path is "on", among `items`: the longest segment-aware prefix
 * match. Segment-aware so `/inventory` does not light up for
 * `/inventory-counts`, longest so a list and its own sub-list are never both
 * highlighted.
 *
 * When no item matches directly, the path may be a screen reached only from
 * inside another one (a tab, a drill-down, a detail page not under its list's
 * path): the longest matching `aliases` entry whose `itemId` is among `items`
 * decides. A direct match always wins over an alias. Restricting aliases to
 * `items` keeps the sidebar — which passes one app's items — from claiming a
 * screen for an app it does not belong to. Pass `[]` for direct matching only.
 */
export function activeNavItem(
  items: readonly NavItem[],
  path: string,
  aliases: readonly NavRouteAlias[] = NAV_ROUTE_ALIASES,
): NavItem | undefined {
  let best: NavItem | undefined
  for (const item of items) {
    if (underPrefix(path, item.to) && (best === undefined || item.to.length > best.to.length)) best = item
  }
  if (best !== undefined) return best

  let bestPrefix = ''
  for (const alias of aliases) {
    if (alias.prefix.length <= bestPrefix.length || !underPrefix(path, alias.prefix)) continue
    const target = items.find((item) => item.id === alias.itemId)
    if (target === undefined) continue
    best = target
    bestPrefix = alias.prefix
  }
  return best
}
